// Replace the entire file content with:
import React from 'react';
import {useParams, Link} from 'react-router-dom';
import {useQuery} from '@tanstack/react-query';
import {
    ArrowLeft, School, Building2, Mail,
    UserCog, CheckCircle2, Clock
} from 'lucide-react';

import {supabase} from '@/services/supabase';
import {LoadingSpinner, Badge} from '@/components/ui';
import {HostList} from '@/components/HostList';
import type {Account, Organization, GameHost} from '@/types';

interface AccountWithOrganization extends Account {
    organizations: Organization;
}

export const AccountDetail: React.FC = () => {
    const {id} = useParams<{ id: string }>();

    // Fetch account detail with game hosts for stats
    const {data: account, isLoading, error} = useQuery({
        queryKey: ['account-detail', id],
        queryFn: async (): Promise<AccountWithOrganization & { game_hosts: GameHost[] }> => {
            if (!id) throw new Error('Account ID is required');

            const {data, error} = await supabase
                .from('accounts')
                .select(`
                    *,
                    organizations!inner(*),
                    game_hosts(
                      id, first_name, last_name, email, invitation_status
                    )
                  `)
                .eq('id', id)
                .eq('account_type', 'academic')
                .is('game_hosts.deleted_at', null)
                .single();

            if (error) throw error;
            return data as any;
        },
        staleTime: 30 * 1000,
    });

    if (isLoading) return <LoadingSpinner size="lg"/>;
    if (error || !account) return <div>Error loading account details</div>;

    const gameHosts: GameHost[] = account.game_hosts || [];
    const registeredCount: number = gameHosts.filter(host => host.invitation_status === 'accepted').length;
    const pendingCount: number = gameHosts.filter(host => host.invitation_status !== 'accepted').length;

    return (
        <div className="space-y-6">
            {/* Breadcrumb */}
            <div className="flex items-center space-x-2 text-sm text-gray-500">
                <Link to="/accounts" className="hover:text-gray-700 flex items-center space-x-1">
                    <ArrowLeft className="h-4 w-4"/>
                    <span>Back to Accounts</span>
                </Link>
            </div>

            {/* Account Header */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-4">
                        <div className="p-3 bg-blue-100 rounded-lg">
                            <School className="h-6 w-6 text-blue-600"/>
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">{account.name}</h1>
                            <div className="mt-2 space-y-1">
                                <div className="flex items-center space-x-2 text-gray-600">
                                    <Building2 className="h-4 w-4"/>
                                    <span>{account.organizations.name}, {account.organizations.state}</span>
                                </div>
                                <div className="flex items-center space-x-2 text-gray-600">
                                    <Mail className="h-4 w-4"/>
                                    <span>{account.contact_person_email}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center space-x-2">
                        <Badge variant={account.status === 'active' ? 'success' : 'default'}>
                            {account.status === 'active' ? 'Active' : 'Inactive'}
                        </Badge>
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Game Hosts</p>
                            <p className="text-2xl font-bold text-gray-900">{gameHosts.length}</p>
                        </div>
                        <UserCog className="h-8 w-8 text-purple-500"/>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Registered</p>
                            <p className="text-2xl font-bold text-green-600">{registeredCount}</p>
                        </div>
                        <CheckCircle2 className="h-8 w-8 text-green-500"/>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Pending</p>
                            <p className="text-2xl font-bold text-yellow-600">{pendingCount}</p>
                        </div>
                        <Clock className="h-8 w-8 text-yellow-500"/>
                    </div>
                </div>
            </div>

            {/* Game Host Management */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Game Hosts</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Manage game hosts for this account
                    </p>
                </div>

                <div className="p-6">
                    <HostList fixedAccountId={id}/>
                </div>
            </div>
        </div>
    );
};
